import { useEffect, useState } from "react";
import api from "../../lib/api";
import { UserCircle2, ArrowRight, Calendar } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "new", label: "New Assignments" },
  { key: "active", label: "Active" },
  { key: "follow_up", label: "Follow-Up Due" },
  { key: "completed", label: "Completed" },
];

export default function AdminMyAssignments() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("new");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/care-coordination/my-assignments/list");
      setRecords(r.data || []);
    } catch (e) {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = records.filter((r) => {
    if (tab === "new") return r.care_stage === "awaiting_acceptance";
    if (tab === "active") return r.care_stage === "active_care";
    if (tab === "follow_up") return r.care_stage === "active_care" && r.follow_up_date && r.follow_up_date <= today;
    if (tab === "completed") return r.care_stage === "completed";
    return true;
  });

  return (
    <div data-testid="admin-my-assignments">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <UserCircle2 className="w-6 h-6 text-[#4A6FA5]" strokeWidth={1.75} /> My Care Assignments
          </h2>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Records assigned to you. Accept or decline new assignments; open a record to view details and record a contact.
          </p>
        </div>
      </div>

      <div className="mt-6 inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`myassign-tab-${t.key}`}
            className={`px-3.5 py-2 text-sm font-medium ${tab === t.key ? "bg-[#4A6FA5] text-white" : "text-slate-700 hover:bg-slate-50"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No records in this tab.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <li key={r.id} className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{r.first_name} {r.last_name} <span className="text-slate-400 font-normal">· {r.company_name}</span></p>
                  <p className="text-xs text-slate-500 mt-1">
                    <Calendar className="inline w-3.5 h-3.5 mr-1" />
                    Received {new Date(r.created_at).toLocaleDateString()}
                    {r.follow_up_date ? ` · Follow-up ${r.follow_up_date}` : ""}
                    {r.next_action ? ` · Next: ${r.next_action}` : ""}
                    {r.priority && r.priority !== "routine" ? ` · ${r.priority.replace("_"," ")}` : ""}
                  </p>
                </div>
                <a
                  href={`/care-portal/admin/care-coordination?open=${r.id}`}
                  data-testid={`myassign-open-${r.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#4A6FA5] hover:bg-blue-50 shrink-0"
                >
                  Open <ArrowRight className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
